import { useMemo, useState } from "react";
import { numerWypowiedzi } from "@shared/numeracja.js";
import { useKatalogParametrow, type StanKatalogu } from "../../hooks/useKatalogParametrow.js";
import { api, type MowcaDto, type SessionDetail, type UtteranceDto } from "../../lib/api.js";
import { odmien } from "../../lib/polski.js";
import { domyslnyWybor, uporzadkujWybor } from "../../lib/parametryOceny.js";
import {
  BEZ_ZMIAN,
  bledyNazw,
  bladZatwierdzenia,
  czyJestZmiana,
  liczbyWypowiedzi,
  mowcaWypowiedzi,
  nazwaPoZmianach,
  podsumowanieZmian,
  przypiszWypowiedz,
  rolaPoZmianach,
  tekstPolaNazwy,
  ustawNazweMowcy,
  ustawRoleMowcy,
  zbudujZatwierdzenie,
  type BladZatwierdzenia,
  type ZmianyPodzialu,
} from "../../lib/podzial.js";
import { bladPodzialuDlaUzytkownika } from "../../lib/wynikUzytkownika.js";
import { IconOstrzezenie } from "../icons/index.js";
import { Avatar } from "../ui/Avatar.js";
import { Badge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { Card } from "../ui/Card.js";
import { type OpcjaSelect, Select } from "../ui/Select.js";
import { Spinner } from "../ui/Spinner.js";
import { StanBledu, StanLadowania, StanPusty } from "../ui/stany.js";
import { useToast } from "../ui/Toast.js";
import { EdytorMowcy } from "./EdytorMowcy.js";
import { WyborParametrow } from "./WyborParametrow.js";

/** Ile wypowiedzi pokazujemy naraz. Dlugi podzial to setki wierszy z listami wyboru. */
const PORCJA = 40;

/** Dla kogo jest ekran: administrator widzi wybor parametrow i koszty, uzytkownik (konto `user`) nie. */
export type WariantPodzialu = "administrator" | "uzytkownik";

/**
 * Ekran zatwierdzania podzialu tekstu (status SPLIT_REVIEW): trzy karty-siostry.
 *
 *   1. "Zatwierdzenie podziału" - zdanie o tym, co sie dzieje, wybor parametrow, podsumowanie
 *      poprawek i przycisk "Zatwierdź i oceń". Stoi NA GORZE, zeby przy dlugim tekscie nie trzeba bylo
 *      przewijac setek wypowiedzi do przycisku.
 *   2. "Mówcy" - nazwy i role.
 *   3. "Podział na wypowiedzi" - kazda wypowiedz w kolorze swojego mowcy, z numerem i wyborem mowcy.
 *
 * Do zatwierdzenia nic nie idzie na serwer: poprawki leza w stanie tego komponentu (patrz
 * lib/podzial.ts), a zatwierdzenie wysyla je razem w jednej transakcji. Odpytywanie sesji nie
 * dotyka tego stanu - jest oddzielony od `sesja`, ktora odswieza sie z serwera.
 *
 * Mowcy sa tylko ci, ktorych zaproponowal model: zatwierdzenie nie tworzy nowych. Gdy tekst ma
 * wiecej mowcow niz model znalazl, trzeba wgrac go jeszcze raz z dokladniejszym opisem.
 *
 * Dwa warianty. "administrator" (domyslny) pokazuje wybor parametrow ze zdaniem o platnych wywolaniach modelu i plakietke
 * "Propozycja AI". "uzytkownik" (konto `user`, pages/uzytkownik/WynikPage.tsx) nie ma wyboru parametrow ani zadnej
 * wzmianki o kosztach i modelu: zatwierdzenie jedzie ze wszystkimi wlaczonymi parametrami (jak w formularzu tego konta,
 * ktory zawsze ocenia komplet), a bledy sa bez kodow technicznych (bladPodzialuDlaUzytkownika). Reszta ekranu jest ta sama.
 */
export function PodzialTekstu({
  sesja,
  odswiez,
  tintDlaId,
  wariant = "administrator",
}: {
  sesja: SessionDetail;
  /** Pobiera sesje jeszcze raz: po zatwierdzeniu status zmienia sie na LENSES i odpytywanie rusza samo. */
  odswiez: () => Promise<void>;
  /** Tint tozsamosci mowcy (1-4) po id: stabilny mimo zmian nazw. */
  tintDlaId: (idMowcy: string) => number;
  wariant?: WariantPodzialu;
}): React.JSX.Element {
  const toast = useToast();
  const dlaUzytkownika = wariant === "uzytkownik";
  const { katalog, odswiez: ponowKatalog } = useKatalogParametrow();

  const [zmiany, setZmiany] = useState<ZmianyPodzialu>(BEZ_ZMIAN);
  const [recznyWybor, setRecznyWybor] = useState<string[] | null>(null);
  const [ile, setIle] = useState(PORCJA);
  const [trwa, setTrwa] = useState(false);
  const [blad, setBlad] = useState<BladZatwierdzenia | null>(null);

  const mowcy = sesja.speakers;
  const wypowiedzi = sesja.utterances;

  const liczby = useMemo(() => liczbyWypowiedzi(mowcy, wypowiedzi, zmiany), [mowcy, wypowiedzi, zmiany]);
  const bledyNazwMowcow = useMemo(() => bledyNazw(mowcy, zmiany, liczby), [mowcy, zmiany, liczby]);

  /**
   * Wybor parametrow: ten z chwili wgrania, a gdy go nie ma - wszystkie wlaczone. null: lista jeszcze nieznana.
   * Uzytkownik niczego nie wybiera: zawsze wszystkie wlaczone.
   */
  const wybrane = useMemo(() => {
    if (katalog.stan !== "gotowy") return null;
    const lista = katalog.katalog.soczewki;
    if (dlaUzytkownika) return domyslnyWybor(lista);
    if (recznyWybor !== null) return uporzadkujWybor(recznyWybor, lista);
    const przedPodzialem = uporzadkujWybor(sesja.podzial?.soczewki.map((s) => s.slug) ?? [], lista);
    return przedPodzialem.length > 0 ? przedPodzialem : domyslnyWybor(lista);
  }, [katalog, recznyWybor, sesja.podzial, dlaUzytkownika]);

  /** Uzytkownik nie moze zatwierdzic, dopoki nie zna listy parametrow do oceny (ladowanie, blad, pusta lista). */
  const brakListy = dlaUzytkownika && (wybrane === null || wybrane.length === 0);

  const opcjeMowcow: OpcjaSelect[] = mowcy.map((m) => ({
    wartosc: m.id,
    etykieta: nazwaPoZmianach(m, zmiany),
  }));

  async function zatwierdz(): Promise<void> {
    if (trwa || brakListy) return;

    // Te same bledy, ktore pokazalibysmy po odpowiedzi serwera (400 nazwa_za_dluga, 409 nazwa_zajeta):
    // lapiemy je przy polach, zanim cokolwiek poleci.
    if (bledyNazwMowcow.size > 0) {
      setBlad({ pole: "mowcy", tekst: "Popraw nazwy mówców zaznaczone w karcie Mówcy: nazwa nie może się powtarzać ani być za długa." });
      return;
    }
    if (wybrane !== null && wybrane.length === 0) {
      setBlad({ pole: "soczewki", tekst: "Zaznacz co najmniej jeden parametr oceny." });
      return;
    }

    setTrwa(true);
    setBlad(null);
    try {
      await api.zatwierdzPodzial(sesja.id, zbudujZatwierdzenie(zmiany, wybrane));
      // TO-1: krotkie potwierdzenie zdarzenia. Stan niesie juz tor postepu na stronie analizy.
      toast.sukces("Podział zatwierdzony. Zaczynamy ocenę.");
      await odswiez();
    } catch (err) {
      const b = dlaUzytkownika ? bladPodzialuDlaUzytkownika(err) : bladZatwierdzenia(err);
      if (b.odswiezParametry) {
        setRecznyWybor(null);
        ponowKatalog();
      }
      if (b.odswiezSesje) await odswiez();
      setBlad(b);
    } finally {
      setTrwa(false);
    }
  }

  const liczbaWypowiedzi = wypowiedzi.length;
  const liczbaMowcow = mowcy.length;
  const widoczne = wypowiedzi.slice(0, ile);
  const zostalo = wypowiedzi.length - widoczne.length;

  return (
    <>
      <Card tytul="Zatwierdzenie podziału">
        <div className="podzial-naglowek">
          {!dlaUzytkownika && <Badge>Propozycja AI</Badge>}
          <span className="podzial-naglowek__liczby">
            {liczbaWypowiedzi} {odmien(liczbaWypowiedzi, "wypowiedź", "wypowiedzi", "wypowiedzi")},{" "}
            {liczbaMowcow} {odmien(liczbaMowcow, "mówca", "mówców", "mówców")}
          </span>
        </div>
        {dlaUzytkownika ? (
          <p className="podzial-lead">
            Podzieliliśmy tekst na wypowiedzi. Sprawdź podział i popraw, jeśli trzeba. Słowa tekstu zostają bez zmian,
            a ocena ruszy po zatwierdzeniu.
          </p>
        ) : (
          <p className="podzial-lead">
            Model podzielił tekst na wypowiedzi i przypisał mówców na podstawie opisu rozmowy. Sprawdź podział
            poniżej, popraw przypisania i nazwy, a potem zatwierdź - dopiero wtedy ruszy ocena. Słowa tekstu zostają
            bez zmian.
          </p>
        )}

        {dlaUzytkownika ? (
          <StanListyDlaUzytkownika katalog={katalog} onPonow={ponowKatalog} />
        ) : (
          <WyborParametrow
            katalog={katalog}
            wybrane={wybrane ?? []}
            onZmiana={(lista) => {
              setRecznyWybor(lista);
              if (blad?.pole === "soczewki") setBlad(null);
            }}
            onPonow={ponowKatalog}
            blad={blad?.pole === "soczewki" ? blad.tekst : undefined}
            wylaczony={trwa}
            etykieta="Co ocenić"
          />
        )}

        <p className="field-hint">{podsumowanieZmian(zmiany)}</p>

        {(blad?.pole === "ogolny" || blad?.pole === "mowcy") && (
          <p className="field-error wysylka-blad" role="alert">
            <IconOstrzezenie size={14} />
            {blad.tekst}
          </p>
        )}

        {/* B-6: akcja, ktora uruchamia ocene (platna) - hero. B-2: to jedyny primary w widoku. */}
        <div className="hero-slot">
          <button
            type="button"
            className="btn-hero"
            aria-disabled={trwa || brakListy || undefined}
            aria-busy={trwa || undefined}
            onClick={() => void zatwierdz()}
          >
            {trwa && <Spinner rozmiar="s" />}
            {trwa ? "Zatwierdzam..." : "Zatwierdź i oceń"}
          </button>
        </div>
      </Card>

      <Card tytul="Mówcy">
        <p className="field-hint podzial-lead">
          Nazwy i role trafiają do oceny i do raportu. Mówca bez wypowiedzi zostanie usunięty przy zatwierdzeniu.
        </p>
        {mowcy.map((m) => (
          <div className="uczestnik" data-tint={tintDlaId(m.id)} key={m.id}>
            <div className="uczestnik__head">
              <span className="msg__avatar">
                <Avatar nazwa={nazwaPoZmianach(m, zmiany)} rozmiar="s" />
              </span>
              <h3 className="uczestnik__nazwa">{nazwaPoZmianach(m, zmiany)}</h3>
              <span className="uczestnik__liczba">
                {liczby.get(m.id) ?? 0}{" "}
                {odmien(liczby.get(m.id) ?? 0, "wypowiedź", "wypowiedzi", "wypowiedzi")}
              </span>
            </div>
            <EdytorMowcy
              idBazowy={`podzial-mowca-${m.id}`}
              etykieta={m.label}
              nazwa={tekstPolaNazwy(m, zmiany)}
              rola={rolaPoZmianach(m, zmiany)}
              onNazwa={(n) => {
                setZmiany((z) => ustawNazweMowcy(z, m, n));
                setBlad(null);
              }}
              onRola={(r) => setZmiany((z) => ustawRoleMowcy(z, m, r))}
              blad={opisBleduNazwy(bledyNazwMowcow.get(m.id))}
              wylaczony={trwa}
            />
          </div>
        ))}
      </Card>

      <Card tytul="Podział na wypowiedzi">
        <p className="field-hint podzial-lead">
          Kolor i awatar pokazują mówcę. Zmiana mówcy przy wypowiedzi jest zapisywana dopiero po zatwierdzeniu
          {czyJestZmiana(zmiany) ? " - poprawki czekają" : ""}.
        </p>
        <ol className="podzial-lista" aria-label="Podział na wypowiedzi">
          {widoczne.map((u) => (
            <WierszPodzialu
              key={u.index}
              wypowiedz={u}
              mowcy={mowcy}
              zmiany={zmiany}
              opcje={opcjeMowcow}
              tint={tintDlaId(mowcaWypowiedzi(u, zmiany))}
              wylaczony={trwa}
              onZmiana={(id) => {
                setZmiany((z) => przypiszWypowiedz(z, u, id));
                setBlad(null);
              }}
            />
          ))}
        </ol>
        {zostalo > 0 && (
          <button type="button" className="rozmowa-wiecej" onClick={() => setIle((n) => n + PORCJA)}>
            {`Pokaż dalsze wypowiedzi (zostało ${zostalo})`}
          </button>
        )}
      </Card>
    </>
  );
}

/**
 * Wariant uzytkownika nie ma wyboru parametrow, ale potrzebuje ich listy, zeby wiedziec, co ocenic. Pokazuje tylko to,
 * co wymaga reakcji: ladowanie, blad z ponowieniem (bez kodu technicznego, w odroznieniu od WyborParametrow) i pusta
 * liste. Gdy lista jest gotowa i niepusta - nic.
 */
function StanListyDlaUzytkownika({
  katalog,
  onPonow,
}: {
  katalog: StanKatalogu;
  onPonow: () => void;
}): React.JSX.Element | null {
  if (katalog.stan === "ladowanie") return <StanLadowania tekst="Przygotowuję ocenę..." />;

  if (katalog.stan === "blad") {
    return (
      <StanBledu
        tytul="Nie udało się przygotować oceny"
        opis="Spróbuj ponownie za chwilę. Jeśli problem wraca, daj nam znać."
        akcja={
          <Button wariant="secondary" onClick={onPonow}>
            Spróbuj ponownie
          </Button>
        }
      />
    );
  }

  if (katalog.katalog.soczewki.length === 0) {
    return <StanPusty tytul="Ocena jest chwilowo niedostępna" opis="Spróbuj ponownie później albo daj nam znać." />;
  }
  return null;
}

function opisBleduNazwy(blad: "za-dluga" | "zajeta" | undefined): string | undefined {
  if (blad === "za-dluga") return "Ta nazwa jest za długa.";
  if (blad === "zajeta") return "Inny mówca ma już taką nazwę. Nadaj im różne nazwy.";
  return undefined;
}

function WierszPodzialu({
  wypowiedz: u,
  mowcy,
  zmiany,
  opcje,
  tint,
  wylaczony,
  onZmiana,
}: {
  wypowiedz: UtteranceDto;
  mowcy: readonly MowcaDto[];
  zmiany: ZmianyPodzialu;
  opcje: readonly OpcjaSelect[];
  tint: number;
  wylaczony: boolean;
  onZmiana: (idMowcy: string) => void;
}): React.JSX.Element {
  const idMowcy = mowcaWypowiedzi(u, zmiany);
  const mowca = mowcy.find((m) => m.id === idMowcy);
  const nazwa = mowca ? nazwaPoZmianach(mowca, zmiany) : u.speaker;
  const numer = numerWypowiedzi(u.index);
  const zmieniona = zmiany.przypisania.has(u.index);

  return (
    <li className="podzial-wiersz" data-tint={tint} data-index={u.index} data-zmieniona={zmieniona || undefined}>
      <span className="podzial-wiersz__numer" aria-label={`Wypowiedź ${numer}`}>
        {numer}
      </span>
      <div className="podzial-wiersz__tresc">
        <div className="msg__ident">
          <span className="msg__avatar">
            <Avatar nazwa={nazwa} rozmiar="s" />
          </span>
          <span className="msg__nazwa">{nazwa}</span>
          {zmieniona && <Badge>zmieniona</Badge>}
        </div>
        <div className="bubble">{u.text}</div>
      </div>
      <div className="podzial-wiersz__mowca">
        <Select
          id={`podzial-mowca-wypowiedzi-${u.index}`}
          aria-label={`Mówca wypowiedzi ${numer}`}
          wartosc={idMowcy}
          opcje={opcje}
          onZmiana={wylaczony ? () => undefined : onZmiana}
        />
      </div>
    </li>
  );
}
